import { Injectable, LOCALE_ID, inject } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { ClassMetadata, PropertyMetadata, MetadataSummary, IGridViewMetadata } from "@tylertech/tyler-api-types";
import { Utilities } from "@tylertech/shared-module";
import { MetadataInputTypeHelperService } from "../metadataInputTypeHelperService";
import { MetadataIntermediary } from "../../interfaces";
import { MetadataService } from "../metadata.service";

@Injectable()
export class GridMetadataService
{
    locale = inject(LOCALE_ID);
    private $metadataService = inject(MetadataService);
    private $metadataInputTypeHelperService = inject(MetadataInputTypeHelperService);

    private addRowSubject$ = new Subject<void>();
    get addRowObservable(): Observable<void>
    {
        return this.addRowSubject$.asObservable();
    }

    canDo(type: string, model: any, isViewOnly: boolean, viewMetadata: IGridViewMetadata): boolean
    {
        const canDo = viewMetadata.GridProperties[`Can${type}`] && !isViewOnly;
        const canDoProp = viewMetadata.GridProperties[`Can${type}Property`];
        if (!canDo) return false;
        if (!Utilities.IsNullOrUndefined(canDoProp))
            return this.$metadataService.EvaluateBoolean(canDoProp, model["$$Parent"], model);
        return canDo;
    }

    setValue(intermediary: MetadataIntermediary): any
    {
        const propertyValue = intermediary.model[intermediary.name];
        if (Utilities.IsNullOrUndefinedOrEmpty(propertyValue))
            return propertyValue;
        if (this.isCurrency(intermediary.property)
            || this.isNumber(intermediary.property)
            || this.isPercent(intermediary.property))
        {
            if (!Number.isNaN(Number(propertyValue)))
                return propertyValue;
            let factor = 1;
            if (propertyValue.includes("(") && propertyValue.includes(")"))
            {
                factor = -1;
            }
            return +propertyValue.replace("$", "")
                .replace("%", "")
                .replace(/,/g, "")
                .replace("(", "")
                .replace(")", "") * factor;
        }

        if (this.isPhoneNumber(intermediary.property))
        {
            return this.stringToPhone(propertyValue);
        }

        if (this.isBoolean(propertyValue, intermediary.property))
        {
            if (this.getCellValue(propertyValue).toLowerCase() === "true") return true;
            if (this.getCellValue(propertyValue).toLowerCase() === "false") return false;
        }
        if (this.isDate(intermediary.property))
            return new Date(propertyValue);
        return propertyValue;
    }

    stringToPhone(str: string): string
    {
        const input = str.replace(/\D/g, "").substring(0, str.length);
        const areaCode = input.substring(0, 3);
        const middle = input.substring(3, 6);
        const last = input.substring(6, 10);

        const ext = input.length > 10 ? input.substring(10, 20) : "";

        if (input.length > 10)
        {
            str = `(${areaCode}) ${middle}-${last} x${ext}`;
        }
        else if (input.length === 10)
        {
            str = `(${areaCode}) ${middle}-${last}`;
        }
        return str;
    }

    isDate(propertyMetadata: PropertyMetadata): boolean
    {
        return Utilities.IsNullOrUndefined(propertyMetadata)
            ? false
            : this.$metadataInputTypeHelperService.isDate(propertyMetadata.InputType) ||
            this.$metadataInputTypeHelperService.isTime(propertyMetadata.InputType) ||
            this.$metadataInputTypeHelperService.isTime24(propertyMetadata.InputType) ||
            this.$metadataInputTypeHelperService.isDateTime(propertyMetadata.InputType);
    }

    isCurrency(propertyMetadata: PropertyMetadata): boolean
    {
        return Utilities.IsNullOrUndefined(propertyMetadata)
            ? false
            : propertyMetadata.InputType === "Currency";
    }

    isPhoneNumber(propertyMetadata: PropertyMetadata): boolean
    {
        return Utilities.IsNullOrUndefined(propertyMetadata)
            ? false
            : propertyMetadata.InputType === "PhoneNumber"
            || propertyMetadata.InputType === "PhoneNumberWithExtension";
    }

    isNumber(propertyMetadata: PropertyMetadata): boolean
    {
        return Utilities.IsNullOrUndefined(propertyMetadata)
            ? false
            : propertyMetadata.InputType === "Number";
    }

    isPercent(propertyMetadata: PropertyMetadata): boolean
    {
        return Utilities.IsNullOrUndefined(propertyMetadata)
            ? false
            : propertyMetadata.InputType === "Percent";
    }

    isBoolean(propertyValue: string, propertyMetadata: PropertyMetadata): boolean
    {
        if (Utilities.IsNullOrUndefined(propertyValue))
            return false;
        return Utilities.IsNullOrUndefined(propertyMetadata)
            ? false
            : propertyMetadata.InputType === "Check";
    }

    getDataType(propertyMetadata: PropertyMetadata): string
    {
        switch (propertyMetadata.InputType)
        {
            case "Percent":
                return "percent";
            case "Currency":
                return "currency";
            case "Number":
                return "number";
            case "NumberWithoutDisplayFiltering":
                return "nonDelineatedNumber";
            case "DateTime":
                return "dateTime";
            case "Date":
                return "date";
            case "Time":
                return "time";
            case "Time24":
                return "time24";
            case "Check":
                return "boolean";
            default:
                return "string";
        }
    }

    getCellValue(value: any): string
    {
        if (Utilities.IsNullOrUndefined(value)) return null;
        if (typeof value === "object")
        {
            return value.Code;
        }
        return value.toString();
    }

    private readonly propertyCache = new Map<string, PropertyMetadata>();

    getProperty(metadata: ClassMetadata, propertyName: string, propertyMetadata?: PropertyMetadata): PropertyMetadata
    {
        if (!metadata)
            return null;
        if (!!propertyMetadata && !propertyMetadata.ClassMetadata)
            return propertyMetadata;
        const cacheKey = `${metadata.TypeName}.${propertyName}`;
        const cached = this.propertyCache.get(cacheKey);
        if (cached)
            return cached;
        const prop = this.findProperty(metadata, propertyName);
        if (prop)
            this.propertyCache.set(cacheKey, prop);
        return prop;
    }

    private findProperty(metadata: ClassMetadata, propertyName: string): PropertyMetadata
    {
        let prop = metadata.Properties.find(x => x.Name === propertyName);
        if (!prop)
            prop = metadata.Properties.find(x => x.Label?.replace(/ /g, "") === propertyName);
        if (!prop)
        {
            for (const property of metadata.Properties)
            {
                if (property.ClassMetadata)
                {
                    prop = this.findProperty(property.ClassMetadata, propertyName);
                    if (prop)
                        break;
                }
            }
        }

        return prop;
    }

    getClassMetadataForProperty(metadata: ClassMetadata, propertyName: string): ClassMetadata
    {
        let prop = metadata.Properties.find(x => x.Name === propertyName);
        if (!prop) prop = metadata.Properties.find(x => !!x.Label && x.Label.replace(/ /g, "") === propertyName);
        if (prop) return metadata;
        let returnMetadata = undefined;
        metadata.Properties.filter(property => property.ClassMetadata).forEach(property =>
        {
            const classMetadata = this.getClassMetadataForProperty(property.ClassMetadata, propertyName);
            if (classMetadata) returnMetadata = classMetadata;
        });
        return returnMetadata;
    }

    getSummaryColumns(classMetadata: ClassMetadata, parent: string = null): string[]
    {
        const self = this;
        if (Utilities.IsNullOrUndefined(classMetadata))
        {
            return [];
        }
        let ret: string[] = [];
        classMetadata.Properties.map(m =>
        {
            const name = parent ? `${parent}.${m.Name}` : m.Name;
            if (m.ClassMetadata)
                ret = ret.concat(self.getSummaryColumns(m.ClassMetadata, name));
            else if (m.SummaryType === MetadataSummary.Sum || m.SummaryType === MetadataSummary.Average)
                ret.push(name);
        });
        return ret;
    }

    getModelForProperty(metadata: ClassMetadata, propertyName: string, model: any): any
    {
        let prop = metadata.Properties.find(x => x.Name === propertyName);
        if (!prop) prop = metadata.Properties.find(x => !!x.Label && x.Label.replace(/ /g, "") === propertyName);
        if (prop) return model;
        let returnModel = undefined;
        metadata.Properties.filter(property => property.ClassMetadata).forEach(property =>
        {
            const currentModel = this.getModelForProperty(property.ClassMetadata, propertyName, model[property.Name]);
            if (currentModel)
            {
                currentModel["$$Parent"] = model;
                returnModel = currentModel;
            }
        });
        return returnModel;
    }

    triggerAddRow(): void
    {
        this.addRowSubject$.next();
    }
}
